<template>
  <base-slide-over size="2xl" ref="slideOver" title="Create Prescription">
    <base-form ref="form" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" v-model="isFormValid">
      <div v-if="locations.length > 1" class="tw-mb-2">
        <div class="tw-mb-3">Location</div>
        <base-select
          label="Practice Location"
          placeholder="Select a location"
          :optgroups="locationOptGroups"
          v-model="selectedLocationId"
          label-key="name"
          value-key="businessLocationId"
          required
        />
      </div>
      <div class="tw-mb-3">Prescription Programs</div>
      <prescription-input
        v-for="prescriptionIndex in prescriptions.length"
        :key="prescriptionIndex"
        :programs="sortedPrograms"
        v-model="prescriptions[prescriptionIndex - 1]"
        :class="['tw-mb-1', { 'tw-border-t tw-pt-6 tw-mb-6': prescriptionIndex > 1 }]"
        :required="!prescriptions.find((p) => !!p.businessProgramId) && prescriptionIndex === 1"
      />
      <div class="tw-flex tw-justify-center tw-py-2">
        <base-button @click="addAnotherProgram" type="flat">Add Another Program</base-button>
      </div>
    </base-form>
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button type="flat" @click="$refs.slideOver.hide()" class="tw-mr-3">Cancel</base-button>
        <base-button @click="createPrescription" :disabled="!isFormValid" :loading="isCreating">Create</base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import PrescriptionInput from "@/views/business/components/PrescriptionInput";
import { orderBy } from "lodash";
import { STATE_OPTIONS } from "@/constants/constants";
import { mapGetters } from "vuex";

export default {
  components: {
    PrescriptionInput,
  },
  props: {
    businessId: String,
    userId: String,
  },
  data() {
    return {
      isCreating: false,
      isFormValid: true,
      selectedLocationId: null,
      prescriptions: [{ businessProgramId: null, medications: [], notes: null }],
      programs: [],
      programGroups: [],
    };
  },
  computed: {
    ...mapGetters("location", ["locations"]),
    locationOptGroups() {
      return STATE_OPTIONS.filter((s) => this.locations.find((l) => l.state === s.value)).map((s) => ({
        label: s.value,
        options: orderBy(this.locations.filter((l) => l.state === s.value), ["name"]),
      }));
    },
    sortedPrograms() {
      if (!this.programGroups.length) return [];

      return this.programGroups
        .slice()
        .sort()
        .filter((pg) => this.programs.some((p) => p.programGroup === pg))
        .map((pg) => ({
          label: pg ? pg : "No Group",
          options: orderBy(this.programs.filter((p) => p.programGroup === pg), ["programGroup", "name"]),
        }));
    },
  },
  created() {
    this.getProgramGroups();
    this.getPrograms();
  },
  methods: {
    show() {
      this.$refs.slideOver.show();
      this.$refs.form.reset();
      this.prescriptions = [{ businessProgramId: null, medications: [], notes: null }];
    },
    hide() {
      this.$refs.slideOver.hide();
    },
    async getPrograms() {
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/list?includeDeleted=false`);
        this.programs = response.data;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve programs",
          message: "We were not able to retrieve your business programs. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      }
    },
    async getProgramGroups() {
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/groups`);
        this.programGroups = response.data;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve program groups",
          message: "We were not able to retrieve your business program groups. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      }
    },
    async createPrescription() {
      if (!this.$refs.form.validate()) return;

      try {
        this.isCreating = true;
        await this.$axios.post(`/businesses/${this.businessId}/prescriptions/${this.userId}`, {
          businessLocationId: this.selectedLocationId,
          prescriptions: this.prescriptions
            .filter((p) => !!p.businessProgramId)
            .map((p) => ({
              ...p,
              businessLocationId: this.selectedLocationId,
              medications: p.medications
                .filter((m) => !!m)
                .map((m, i) => ({
                  businessItemCategoryId: m,
                  sortOrder: i,
                })),
              notes: [p.notes],
            })),
        });
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Prescription Created", message: "The prescription has been submitted successfully." });
        this.hide();
        this.$emit("created");
      } catch (error) {
        if (error.response.data[0].message == "User already has prescriptions for this program") {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Patient already has active plan for this program",
            message: `If you are attempting refill a prescription for this patient please goto Activities to manage your refill authorizations.`,
            error,
          });
        } else {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to sign up",
            message: `We were not able to sign up ${this.fullName}. Please try again.`,
            error,
          });
        }
        throw error;
      } finally {
        this.isCreating = false;
      }
    },
    addAnotherProgram() {
      this.prescriptions.push({
        businessProgramId: null,
        medications: [],
        notes: null,
      });
    },
  },
  watch: {
    locations: {
      immediate: true,
      handler() {
        if (this.locations.length === 1) this.selectedLocationId = this.locations[0].businessLocationId;
      },
    },
  },
};
</script>
